import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import {
  AnalysisFeedbackDraft,
  AnalysisFeedbackField,
  AnalysisFeedbackFieldErrors,
  AnalysisFeedbackRating,
  AnalysisFeedbackReasonCode,
  AnalysisFeedbackRecord,
  AnalysisFeedbackSaveResult,
  AnalysisFeedbackValidationError
} from '../analysis-feedback-types';

const EMPTY_DRAFT: AnalysisFeedbackDraft = { rating: null, reasons: [], comment: '' };

interface UseAnalysisFeedbackFormProps {
  feedback: AnalysisFeedbackRecord | null;
  initialRating: AnalysisFeedbackRating | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (draft: AnalysisFeedbackDraft) => Promise<AnalysisFeedbackSaveResult | void> | void;
}

const getDraft = (
  feedback: AnalysisFeedbackRecord | null,
  initialRating: AnalysisFeedbackRating | null
): AnalysisFeedbackDraft =>
  feedback
    ? { rating: feedback.rating, reasons: feedback.reasons, comment: feedback.comment }
    : { ...EMPTY_DRAFT, rating: initialRating };

export const useAnalysisFeedbackForm = ({
  feedback,
  initialRating,
  isOpen,
  onClose,
  onSave
}: UseAnalysisFeedbackFormProps) => {
  const intl = useIntl();
  const commentRef = useRef<HTMLTextAreaElement | null>(null);
  const focusFirstElement = useRef<HTMLElement | null>(null);
  const wasOpenRef = useRef(false);
  const [draft, setDraft] = useState<AnalysisFeedbackDraft>(EMPTY_DRAFT);
  const [fieldErrors, setFieldErrors] = useState<AnalysisFeedbackFieldErrors>({});
  const [focusInvalidField, setFocusInvalidField] = useState<AnalysisFeedbackField | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const resetDraft = useCallback(() => {
    setDraft(getDraft(feedback, initialRating));
    setFieldErrors({});
    setSubmitError(null);
  }, [feedback, initialRating]);

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      resetDraft();
      setShowSuccess(false);
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, resetDraft]);

  useEffect(() => {
    if (isOpen && feedback) {
      resetDraft();
    }
  }, [feedback, isOpen, resetDraft]);

  useEffect(() => {
    if (isSubmitting || !focusInvalidField) {
      return;
    }

    if (focusInvalidField === 'comment') {
      commentRef.current?.focus();
    } else {
      focusFirstElement.current?.focus();
    }
    setFocusInvalidField(null);
  }, [focusInvalidField, isSubmitting]);

  const isEditableByAuthor = !!feedback?.can_edit && feedback.submitted_by_current_user;
  const isReadOnly = !!feedback && (!isEditableByAuthor || !feedback.is_active);

  const setFirstControlRef = useCallback((element: HTMLElement | null) => {
    focusFirstElement.current = element;
  }, []);

  const handleCancel = useCallback(() => {
    if (!isSubmitting) {
      resetDraft();
      onClose();
    }
  }, [isSubmitting, onClose, resetDraft]);

  const handleRatingChange = useCallback((rating: AnalysisFeedbackRating) => {
    setFieldErrors(current => ({ ...current, rating: undefined, reasons: undefined }));
    setDraft(current => ({
      ...current,
      rating,
      reasons: current.rating === rating ? current.reasons : []
    }));
  }, []);

  const handleReasonChange = useCallback((reason: AnalysisFeedbackReasonCode) => {
    setFieldErrors(current => ({ ...current, reasons: undefined }));
    setDraft(current => ({
      ...current,
      reasons: current.reasons.includes(reason)
        ? current.reasons.filter(selectedReason => selectedReason !== reason)
        : [...current.reasons, reason]
    }));
  }, []);

  const handleCommentChange = useCallback((comment: string) => {
    setFieldErrors(current => ({ ...current, comment: undefined }));
    setDraft(current => ({ ...current, comment }));
  }, []);

  const handleSaveResult = useCallback(
    (result: AnalysisFeedbackSaveResult | void) => {
      if (!result || result.status === 'saved') {
        setShowSuccess(true);
      } else if (result.status === 'feedback_already_exists') {
        setDraft(getDraft(result.feedback, initialRating));
        setSubmitError(
          intl.formatMessage({
            id: 'aiInsights.feedback.alreadyExists',
            defaultMessage: 'Feedback was submitted while this panel was open. We’ve loaded the saved feedback.'
          })
        );
      } else if (result.status === 'feedback_superseded') {
        setDraft(getDraft(result.feedback, initialRating));
        setSubmitError(
          intl.formatMessage({
            id: 'aiInsights.feedback.conflictSuperseded',
            defaultMessage: 'Newer feedback is available. We’ve loaded it below.'
          })
        );
      } else if (result.status === 'stale_analysis') {
        setSubmitError(
          intl.formatMessage({
            id: 'aiInsights.feedback.staleAnalysis',
            defaultMessage: 'A newer business analysis is available. We’re refreshing it now.'
          })
        );
      }
    },
    [initialRating, intl]
  );

  const handleSubmitFailure = useCallback(
    (error: unknown) => {
      if (error instanceof AnalysisFeedbackValidationError) {
        const validationErrors = error.fieldErrors;
        const firstInvalidField = (['rating', 'reasons', 'comment'] as AnalysisFeedbackField[]).find(
          field => validationErrors[field]?.length
        );
        setFieldErrors(validationErrors);
        setFocusInvalidField(firstInvalidField ?? null);
        return;
      }

      setSubmitError(
        intl.formatMessage({
          id: 'aiInsights.feedback.unexpectedError',
          defaultMessage: 'We couldn’t save your feedback. Your changes are still here, so please try again.'
        })
      );
    },
    [intl]
  );

  const handleSubmit = useCallback(async () => {
    if (!draft.rating || isSubmitting || isReadOnly) {
      return;
    }

    setIsSubmitting(true);
    setFieldErrors({});
    setSubmitError(null);

    try {
      const result = await onSave(draft);
      handleSaveResult(result);
    } catch (error: unknown) {
      handleSubmitFailure(error);
    } finally {
      setIsSubmitting(false);
    }
  }, [draft, handleSaveResult, handleSubmitFailure, isReadOnly, isSubmitting, onSave]);

  return {
    commentRef,
    draft,
    fieldErrors,
    focusFirstElement,
    handleCancel,
    handleCommentChange,
    handleRatingChange,
    handleReasonChange,
    handleSubmit,
    isEditableByAuthor,
    isReadOnly,
    isSubmitting,
    setFirstControlRef,
    showSuccess,
    submitError
  };
};
